import { useEffect, useRef, useState } from 'react';

/**
 * 실제 로딩(isLoading)이 0.7초보다 빨리 끝나도, 로딩 화면을 최소 minMs 동안 유지.
 *
 * 반환값(showLoading)이 true인 동안 LoadingView를 렌더링하고, false가 되면 페이지를 렌더링하면 된다.
 *
 *
 * @param isLoading 실제 데이터 로딩 여부
 * @param minMs 로딩 화면 최소 노출 시간(ms). 기본 700.
 */
export function useMinimumLoading(isLoading: boolean, minMs = 500): boolean {
  const [showLoading, setShowLoading] = useState(isLoading);
  // 로딩이 시작된 시각. 로딩 중이 아닐 땐 null.
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      // 로딩 시작: 시작 시각을 한 번만 기록하고 로딩 화면 노출.
      if (startRef.current === null) startRef.current = Date.now();
      setShowLoading(true);
      return;
    }

    // 로딩 종료: 실제로 로딩한 적이 없으면 최소 시간 없이 바로 표시.
    if (startRef.current === null) {
      setShowLoading(false);
      return;
    }

    // 최소 노출 시간이 남았으면 그만큼 더 유지한 뒤 페이지 렌더링.
    const remaining = minMs - (Date.now() - startRef.current);
    if (remaining <= 0) {
      startRef.current = null;
      setShowLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      startRef.current = null;
      setShowLoading(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [isLoading, minMs]);

  return showLoading;
}
